import React from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const Privacy = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="legal-page-container">
      <SEO
        title="Privacy Policy"
        description="Star.Ryhwa privacy policy. Learn how we safeguard customer data and transactional privacy."
        canonicalUrl="/privacy"
      />
      <div className="legal-page-inner">
        {/* Breadcrumb Bar */}
        <div className="legal-breadcrumbs">
          <Link to="/">Home</Link>
          <span className="legal-breadcrumb-separator">/</span>
          <span>Trust &amp; Legal Center</span>
          <span className="legal-breadcrumb-separator">/</span>
          <span className="legal-breadcrumb-current">Privacy Policy</span>
        </div>

        {/* Document Header */}
        <header className="legal-doc-header">
          <h1 className="legal-doc-title">Privacy Policy</h1>
          <p className="legal-doc-description">
            Transparent disclosure of how Star.Ryhwa collects, protects, and strictly minimizes personal information across our handmade studio and e-commerce services.
          </p>

          <div className="legal-meta-bar">
            <div className="legal-meta-items">
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Last Updated: <strong>September 2026</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Estimated Read: <strong>5 min read</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
                Jurisdiction: <strong>State of Hawaii, USA</strong>
              </span>
              <span className="legal-meta-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Security: <strong>Square PCI-DSS Level 1</strong>
              </span>
            </div>
            <button type="button" onClick={handlePrint} className="legal-print-btn" title="Print or Save as PDF">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / Save PDF
            </button>
          </div>
        </header>

        {/* Navigation Tab Bar */}
        <LegalNav />

        {/* Executive Summary */}
        <div className="legal-doc-summary">
          <h2>Summary of Key Principles</h2>
          <ul>
            <li><strong>Zero Commercial Data Selling:</strong> We never sell, rent, monetize, or broker your personal information to third parties.</li>
            <li><strong>No Invasive Advertising Trackers:</strong> We do not deploy third-party advertising cookies or cross-site tracking pixels. Browser local storage is used solely to maintain your active shopping cart.</li>
            <li><strong>Encrypted Level 1 Payment Processing:</strong> Payments are processed directly through Square, Inc. Star.Ryhwa never touches, views, or stores your raw card numbers.</li>
            <li><strong>COPPA Compliant:</strong> Minors under 18 must order with parent or guardian supervision. We never knowingly collect data from children under 13.</li>
          </ul>
        </div>

        {/* Main Two-Column Layout */}
        <div className="legal-grid-layout">
          {/* Main Content Column */}
          <article className="legal-main-content">
            {/* Section 1 */}
            <section id="section-1" className="legal-doc-section">
              <h2>1. Overview &amp; Privacy Commitment</h2>
              <p>
                Star.Ryhwa ("we," "us," or "our") operates this online artisanal studio and is committed to protecting your privacy with utmost integrity and transparency. We practice strict data minimization: we only collect the personal information strictly necessary to craft your custom items, process your payments, and ship your orders.
              </p>
              <p>
                By accessing this website, purchasing products, or communicating with us, you acknowledge and agree to the practices outlined in this Privacy Policy.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="legal-doc-section">
              <h2>2. Categories of Personal Information We Collect</h2>
              <p>
                Depending on how you interact with our store, we may collect the following categories of personal information:
              </p>

              <div className="legal-table-wrapper">
                <table className="legal-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Data Elements Collected</th>
                      <th>Business Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Identifiers</strong></td>
                      <td>Full name, delivery/shipping address, email address.</td>
                      <td>Order fulfillment, tracking notifications, and direct customer support.</td>
                    </tr>
                    <tr>
                      <td><strong>Commercial Records</strong></td>
                      <td>Products purchased, quantities, order date, and custom personalization notes (e.g., bias, member, theme, or color choices).</td>
                      <td>Crafting custom handmade items, inventory management, and tax accounting.</td>
                    </tr>
                    <tr>
                      <td><strong>Payment Data</strong></td>
                      <td>Tokenized transaction identifiers, billing zip code, payment status.</td>
                      <td>Processed directly by Square, Inc. <em>(Star.Ryhwa never collects, views, or stores full card numbers or security CVV codes).</em></td>
                    </tr>
                    <tr>
                      <td><strong>Device &amp; Session Data</strong></td>
                      <td>Shopping cart contents, browser session state saved in local browser storage.</td>
                      <td>Enabling cart persistence across browsing sessions on your device.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="legal-doc-section">
              <h2>3. Children's Online Privacy Protection (COPPA Notice)</h2>
              <div className="legal-doc-note">
                <p>
                  <strong>Protection of Minors:</strong> Star.Ryhwa does not knowingly collect, solicit, or maintain personal information from children under the age of 13 without verified parental or legal guardian consent.
                </p>
              </div>
              <p>
                Our products (including K-Pop collectibles, stickers, and decorative keychains) may appeal to young audiences; however, our checkout and purchasing services are intended strictly for individuals who are at least 18 years of age or who have explicit permission and supervision from a parent or legal guardian.
              </p>
              <p>
                If you believe a child under 13 has submitted personal contact or address information to us without parental consent, please contact us immediately at <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a>, and we will promptly purge that information from our records.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="legal-doc-section">
              <h2>4. Payment Security &amp; Subprocessors</h2>
              <p>
                We do not sell, rent, lease, or monetize your personal information. We disclose personal information solely to vetted service providers (subprocessors) necessary to run our studio:
              </p>

              <div className="legal-doc-note">
                <p>
                  <strong>PCI-DSS Level 1 Payment Security:</strong> All credit card transactions are handled exclusively by <strong>Square, Inc.</strong> Your raw payment card numbers and security codes never touch or reside on Star.Ryhwa servers. For details, review <a href="https://squareup.com/us/en/legal/general/privacy" target="_blank" rel="noopener noreferrer">Square's Privacy Notice</a>.
                </p>
              </div>

              <ul>
                <li><strong>Shipping Carriers (e.g., USPS):</strong> We provide your name and delivery address to postal carriers solely to generate postage labels and dispatch your package.</li>
                <li><strong>Hosting &amp; Infrastructure:</strong> Our site is delivered over encrypted HTTPS protocols via reputable cloud infrastructure providers that process temporary network requests.</li>
                <li><strong>Direct Communication:</strong> If you reach out via email or direct message, your correspondence is used strictly to answer your inquiries and coordinate custom orders.</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="legal-doc-section">
              <h2>5. Notice to European (GDPR), UK, and International Customers</h2>
              <p>
                Star.Ryhwa is located in the State of Hawaii, United States. When you place an order from outside the United States, your personal data (name, email, shipping address) will be transferred to, stored, and processed in the United States.
              </p>
              <p><strong>Lawful Basis for Processing (GDPR Art. 6):</strong></p>
              <ul>
                <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Processing your name, address, and payment confirmation is strictly required to execute the sales contract and deliver your goods.</li>
                <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> We retain sales records as necessary to satisfy state, federal, and customs reporting regulations.</li>
              </ul>
              <p>
                If you reside in the EU, UK, or Switzerland, you have the right under applicable data protection laws to request access to, rectification of, or erasure of your personal data, as well as the right to restrict or object to processing. To exercise these rights, please email our privacy desk.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="legal-doc-section">
              <h2>6. California Consumer Privacy Rights (CCPA / CPRA Notice)</h2>
              <p>
                If you are a California resident, the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA) grants you specific rights:
              </p>
              <ul>
                <li><strong>Right to Know / Access:</strong> The right to request the categories and specific pieces of personal information we have collected about you over the past 12 months.</li>
                <li><strong>Right to Delete:</strong> The right to request that we delete personal information collected from you, subject to legal record-keeping exceptions (such as fulfilling an ongoing order or tax records).</li>
                <li><strong>Right to Correct:</strong> The right to request that we rectify inaccurate personal information.</li>
                <li><strong>Right to Non-Discrimination:</strong> We will never deny goods, charge different prices, or provide a lower quality of service for exercising your privacy rights.</li>
                <li><strong>"Do Not Sell or Share My Personal Information":</strong> Star.Ryhwa does not sell personal information or share personal information for cross-context behavioral advertising.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="legal-doc-section">
              <h2>7. Cookies, Local Storage &amp; Tracking</h2>
              <p>
                Star.Ryhwa respects your digital footprint. We do <strong>not</strong> employ invasive third-party advertising trackers, cross-site tracking beacons, or commercial advertising cookies.
              </p>
              <p>
                This website uses your browser's native <strong>Local Storage</strong> solely for essential functionality—specifically, saving the items in your shopping cart so you do not lose your selections if you refresh the page or navigate between products. You can clear this data at any time directly through your web browser's cache settings.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="legal-doc-section">
              <h2>8. Data Retention Schedule</h2>
              <p>
                We retain your personal information only as long as necessary to accomplish the purposes described in this policy:
              </p>
              <ul>
                <li><strong>Active Orders:</strong> Retained during production, transit, and throughout the 7-day damage claim and delivery inspection window.</li>
                <li><strong>Financial &amp; Tax Compliance:</strong> Basic transaction summaries (order totals, tax calculations, shipping records) are retained in our financial ledgers solely to comply with federal, state (Hawaii GET), and local accounting laws.</li>
                <li><strong>Customer Deletion Requests:</strong> Upon written request, non-essential personal identifiers will be permanently deleted once all outstanding order obligations are fulfilled.</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="legal-doc-section">
              <h2>9. Privacy Inquiries &amp; Customer Support</h2>
              <p>
                For questions regarding this Privacy Policy, data access or deletion requests, or our general handling practices, please contact our studio team:
              </p>
              <ul className="legal-contact-list">
                <li><strong>Email:</strong> <a href="mailto:officialstar.ryhwa@gmail.com">officialstar.ryhwa@gmail.com</a></li>
                <li><strong>Instagram:</strong> <a href="https://www.instagram.com/star.ryhwa" target="_blank" rel="noopener noreferrer">@star.ryhwa</a></li>
                <li><strong>Studio Location:</strong> State of Hawaii, United States</li>
              </ul>
            </section>
          </article>

          {/* Sticky Table of Contents Sidebar */}
          <aside className="legal-sidebar">
            <div className="legal-toc-container">
              <div className="legal-toc-title">On This Page</div>
              <ul className="legal-toc-list">
                <li><a href="#section-1" className="legal-toc-link">1. Overview &amp; Commitment</a></li>
                <li><a href="#section-2" className="legal-toc-link">2. Data We Collect</a></li>
                <li><a href="#section-3" className="legal-toc-link">3. Children's Privacy (COPPA)</a></li>
                <li><a href="#section-4" className="legal-toc-link">4. Payment &amp; Subprocessors</a></li>
                <li><a href="#section-5" className="legal-toc-link">5. European &amp; UK Rights (GDPR)</a></li>
                <li><a href="#section-6" className="legal-toc-link">6. California Rights (CPRA)</a></li>
                <li><a href="#section-7" className="legal-toc-link">7. Cookies &amp; Local Storage</a></li>
                <li><a href="#section-8" className="legal-toc-link">8. Data Retention Schedule</a></li>
                <li><a href="#section-9" className="legal-toc-link">9. Inquiries &amp; Support</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
